import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppSingleSelectCheckboxGroup from '@/common/components/form-inputs/AppSingleSelectCheckboxGroup';
import { CONSENT_OBTAINED_LIST } from '@/common/constants/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditCaseStatusForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			consentObtained: null,
		};

		useEffect(() => {
			if (details) {
				methods.reset(details);
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			consentObtained: Yup.number()
				.nullable()
				.required(t('errorMessage.pleaseSelect')),
			receivingReferralOrApplicationDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			defaultedPsychiatricFollowUpSince: Yup.string().when(
				'defaultedPsychiatricFollowUp',
				{
					is: (is: boolean) => is,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
			regularPsychiatricFollowUpSince: Yup.string().when(
				'hasRegularPsychiatricFollowUp',
				{
					is: (is: boolean) => is,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchDefaultedPsychiatricFollowUp = methods.watch(
			'defaultedPsychiatricFollowUp'
		);
		const watchHasRegularPsychiatricFollowUp = methods.watch(
			'hasRegularPsychiatricFollowUp'
		);

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const onSubmit: SubmitHandler<any> = async (data) => {};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.caseStatus')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={2} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<SingleCheckbox
									control={methods.control}
									name='suspectedMentalHealthProblems'
									label={t('preAdmission.suspectedMentalHealthProblems')}
								/>
							</Grid>
							<Grid size={6}>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='defaultedPsychiatricFollowUp'
										label={t('preAdmission.defaultedPsychiatricFollowUpSince')}
										handleChange={() => {
											methods.setValue('defaultedPsychiatricFollowUpSince', '');
											methods.clearErrors('defaultedPsychiatricFollowUpSince');
										}}
									/>
									<AppTextField
										disabled={!watchDefaultedPsychiatricFollowUp}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='defaultedPsychiatricFollowUpSince'
										label={`${t('preAdmission.since')}${
											watchDefaultedPsychiatricFollowUp ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={6}>
								<SingleCheckbox
									control={methods.control}
									name='awaitingPsychiatricFollowUp'
									label={t('preAdmission.awaitingPsychiatricFollowUp')}
								/>
							</Grid>
							<Grid size={6}>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='hasRegularPsychiatricFollowUp'
										label={t('preAdmission.hasRegularPsychiatricFollowUpSince')}
										handleChange={() => {
											methods.setValue('regularPsychiatricFollowUpSince', '');
											methods.clearErrors('regularPsychiatricFollowUpSince');
										}}
									/>
									<AppTextField
										disabled={!watchHasRegularPsychiatricFollowUp}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='regularPsychiatricFollowUpSince'
										label={`${t('preAdmission.since')}${
											watchHasRegularPsychiatricFollowUp ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.consentObtained') + '*'}
								</Typography>
								<AppSingleSelectCheckboxGroup
									control={methods.control}
									name='consentObtained'
									row
									options={CONSENT_OBTAINED_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='receivingReferralOrApplicationDate'
									label={t('preAdmission.dateOfReceivingReferral/Application')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='enquiryRecordRemarks'
									label={t('preAdmission.remarks(EnquiryRecord)')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='membershipApplicationRemarks'
									label={t('preAdmission.remarks(MembershipApplication)')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='faceSheetRemarks'
									label={t('preAdmission.remarks(FaceSheet)')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditCaseStatusForm.displayName = 'EditCaseStatusForm';

export default EditCaseStatusForm;
